import { Button } from "@/components/ui/button"
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog"
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form"
import { Input } from "@/components/ui/input"
import { Textarea } from "@/components/ui/textarea"
import { useCreateService } from "@/services/query/services.query"
import { useForm, useFieldArray } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import * as z from "zod"
import { useToast } from "@/components/ui/use-toast"
import PropTypes from "prop-types"
import { Loader2, Plus, X } from "lucide-react"
import { useState } from "react"

const formSchema = z.object({
  title: z.string().min(2, "Title must be at least 2 characters"),
  descriptions: z.array(
    z.object({
      key: z.string().min(1, "Key is required"),
      value: z.string().min(10, "Value must be at least 10 characters"),
    })
  ),
  meta: z.object({}).default({}),
})

export function AddServiceDialog({ open, onOpenChange }) {
  const { toast } = useToast()
  const createServiceMutation = useCreateService()

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      title: "",
      descriptions: [{ key: "", value: "" }],
      meta: {},
    },
  })

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "descriptions",
  })

  const onSubmit = async (values) => {
    try {
      // Transform descriptions array to object
      const description = values.descriptions.reduce((acc, { key, value }) => {
        acc[key] = value
        return acc
      }, {})

      await createServiceMutation.mutateAsync({
        title: values.title,
        description,
        meta: values.meta,
      })
      toast({
        title: "Success",
        description: "Service created successfully",
        className: "bg-green-50 border-green-500",
      })
      form.reset()
      onOpenChange(false)
    } catch (error) {
      toast({
        title: "Error",
        description: error.message || "Failed to create service",
        variant: "destructive",
        duration: 5000,
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[800px]">
        <DialogHeader>
          <DialogTitle className="text-xl font-semibold">
            Add New Service
          </DialogTitle>
          <DialogDescription>
            Create a new service by filling out the information below.
          </DialogDescription>
        </DialogHeader>
        <Form {...form}>
          <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-6">
            <FormField
              control={form.control}
              name="title"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>Service Title</FormLabel>
                  <FormControl>
                    <Input
                      placeholder="Enter service title"
                      {...field}
                      className="focus-visible:ring-blue-500"
                    />
                  </FormControl>
                  <FormMessage />
                </FormItem>
              )}
            />

            <div className="space-y-4">
              <div className="flex items-center justify-between">
                <FormLabel className="text-lg font-semibold">
                  Description Sections
                </FormLabel>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => append({ key: "", value: "" })}
                  className="flex items-center gap-2 border-blue-500 text-blue-500 hover:bg-blue-50"
                >
                  <Plus className="h-4 w-4" /> Add Section
                </Button>
              </div>

              <div className="space-y-4">
                {fields.map((field, index) => (
                  <div
                    key={field.id}
                    className="grid grid-cols-12 gap-4 items-start p-4 rounded-lg border border-gray-200 bg-gray-50"
                  >
                    <div className="col-span-3">
                      <FormField
                        control={form.control}
                        name={`descriptions.${index}.key`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Section Title
                            </FormLabel>
                            <FormControl>
                              <Input
                                placeholder="e.g., DIAGNOSIS"
                                {...field}
                                className="focus-visible:ring-blue-500 bg-white"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="col-span-8">
                      <FormField
                        control={form.control}
                        name={`descriptions.${index}.value`}
                        render={({ field }) => (
                          <FormItem>
                            <FormLabel className="text-sm font-medium text-gray-700">
                              Content
                            </FormLabel>
                            <FormControl>
                              <Textarea
                                placeholder="Enter detailed description"
                                {...field}
                                className="min-h-[100px] focus-visible:ring-blue-500 bg-white"
                              />
                            </FormControl>
                            <FormMessage />
                          </FormItem>
                        )}
                      />
                    </div>
                    <div className="col-span-1 flex justify-end pt-8">
                      {fields.length > 1 && (
                        <Button
                          type="button"
                          variant="ghost"
                          size="icon"
                          onClick={() => remove(index)}
                          className="hover:bg-red-100 hover:text-red-500 rounded-full h-8 w-8"
                        >
                          <X className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </div>

            <DialogFooter className="gap-2 pt-4 border-t">
              <Button
                type="button"
                variant="outline"
                onClick={() => {
                  form.reset()
                  onOpenChange(false)
                }}
                disabled={createServiceMutation.isPending}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                disabled={createServiceMutation.isPending}
                className={`min-w-[120px] relative ${
                  createServiceMutation.isPending
                    ? "bg-blue-400"
                    : "bg-blue-500 hover:bg-blue-600"
                }`}
              >
                {createServiceMutation.isPending ? (
                  <div className="flex items-center">
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Creating...
                  </div>
                ) : (
                  "Create Service"
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}

AddServiceDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
}
